/**
 * Bell-icon polling hook — calls `/api/approvals/pending/summary` every
 * 30 s while the tab is visible.  The endpoint is cheap (count + ≤5
 * latest items, no diff payload) so the cadence is safe.
 */
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import { getPendingSummary, type PendingSummary } from "@/api/approvals";

export const APPROVALS_BELL_KEY = ["approvals", "pending", "summary"] as const;

const EMPTY: PendingSummary = { count: 0, latest: [] };

export interface UseApprovalsBell {
  data: PendingSummary;
  isLoading: boolean;
  isError: boolean;
}

export function useApprovalsBell(): UseApprovalsBell {
  const query = useQuery<PendingSummary, Error>({
    queryKey: APPROVALS_BELL_KEY,
    queryFn: getPendingSummary,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 401) return false;
      return failureCount < 1;
    },
    staleTime: 15_000,
  });
  return {
    data: query.data ?? EMPTY,
    isLoading: query.isLoading,
    isError: Boolean(query.error),
  };
}
